import React, { createContext, useContext, useEffect, useState } from "react";
import { currencyToLocale } from "../utils/currencies";
import { useAuth } from "../auth/AuthContext";
import { useUserProfileContext } from "./ProfileContext";

type CurrencyContextType = {
  currency: string;
  setCurrency: (newCurrency: string) => void;
  formatCurrencyNumber: (value: number) => string;
  formatCompactCurrencyNumber: (value: number) => string;
  currencyLocale: string | undefined;
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currency, setCurrency] = useState<string>("USD");
  const { isAuthenticated } = useAuth();
  const locale = currencyToLocale[currency] || undefined;
  
  // Use React Query hook to fetch user profile
  // Only fetch when authenticated to avoid 403 errors on public pages
  const { userProfile } = useUserProfileContext();

  // Update currency when user profile is loaded
  useEffect(() => {
    if (isAuthenticated && userProfile?.currency) {
      setCurrency(userProfile.currency);
    } else if (!isAuthenticated) {
      // Reset to default when logged out
      setCurrency("USD");
    }
  }, [isAuthenticated, userProfile]);

  const formatCurrencyNumber = (value: number): string => {
    const hasDecimals = value % 1 !== 0;

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const buildCompactNumber = (value: number): string => {
    const absoluteValue = Math.abs(value);

    if (absoluteValue < 1000) {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(value);
    }

    const scales = [
      { divisor: 1e12, suffix: "T" },
      { divisor: 1e9, suffix: "B" },
      { divisor: 1e6, suffix: "M" },
      { divisor: 1e3, suffix: "k" },
    ];

    const selectedScale = scales.find((scale) => absoluteValue >= scale.divisor) || scales[scales.length - 1];
    const compactValue = value / selectedScale.divisor;
    const compactAbs = Math.abs(compactValue);

    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: compactAbs >= 100 ? 0 : compactAbs >= 10 ? 1 : 2,
    }).format(compactValue);

    return `${formattedNumber}${selectedScale.suffix}`;
  };

  const formatCompactWithCurrency = (value: number): string => {
    const compactNumber = buildCompactNumber(value);
    const currencyParts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(1);

    const currencyPart = currencyParts.find((part) => part.type === "currency")?.value || currency;
    const currencyIndex = currencyParts.findIndex((part) => part.type === "currency");
    const numberIndex = currencyParts.findIndex((part) => part.type === "integer");
    const hasSpacingLiteral = currencyParts.some((part) => part.type === "literal" && part.value.trim() === "");
    const separator = hasSpacingLiteral ? " " : "";

    if (currencyIndex !== -1 && numberIndex !== -1 && currencyIndex < numberIndex) {
      return `${currencyPart}${separator}${compactNumber}`;
    }

    return `${compactNumber}${separator}${currencyPart}`;
  };

  const formatCompactCurrencyNumber = (value: number): string => {
    return formatCompactWithCurrency(value);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        formatCurrencyNumber,
        formatCompactCurrencyNumber,
        currencyLocale: locale
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrencyFormatter = (): CurrencyContextType => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error("useCurrencyFormatter must be used within CurrencyProvider");
  }
  return ctx;
};

